import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Globe, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, TrendingUp, Server } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface UptimeCheck {
  id: string;
  url: string;
  timestamp: number;
  status: 'up' | 'down' | 'checking';
  responseTime: number;
  statusCode: number;
  error?: string;
}

interface UptimeStats {
  uptime: number;
  totalChecks: number;
  successfulChecks: number;
  averageResponseTime: number;
  lastDowntime?: number;
}

const WebsiteUptimeChecker: React.FC = () => {
  const [url, setUrl] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [checks, setChecks] = useState<UptimeCheck[]>([]);
  const [monitoringInterval, setMonitoringInterval] = useState<NodeJS.Timeout | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [checkInterval, setCheckInterval] = useState(300); // 5 minutes in seconds
  const [stats, setStats] = useState<UptimeStats>({
    uptime: 0,
    totalChecks: 0,
    successfulChecks: 0,
    averageResponseTime: 0
  });

  useSEO({
    title: 'Website Uptime Checker - Monitor Website Availability',
    description: 'Monitor website uptime and availability. Check if your website is down or slow. Get real-time uptime monitoring and performance stats.',
    keywords: 'uptime checker, website monitor, website availability, downtime detector, server monitoring, website status',
    canonical: 'https://snap-tools.online/seo/website-uptime-checker'
  });

  // Check website status
  const checkWebsite = async (targetUrl?: string): Promise<UptimeCheck> => {
    const checkUrl = targetUrl || url;
    const startTime = Date.now();
    
    const newCheck: UptimeCheck = {
      id: Math.random().toString(36).substr(2, 9),
      url: checkUrl,
      timestamp: startTime,
      status: 'checking',
      responseTime: 0,
      statusCode: 0
    };

    setChecks(prev => [newCheck, ...prev.slice(0, 99)]); // Keep last 100 checks

    try {
      // In a real implementation, you would use a CORS proxy or backend service
      // For demo purposes, we'll simulate the check
      await new Promise(resolve => setTimeout(resolve, Math.random() * 2000 + 500));
      
      const responseTime = Date.now() - startTime;
      const statusCode = Math.random() > 0.1 ? 200 : Math.random() > 0.5 ? 404 : 500;
      const status = statusCode >= 200 && statusCode < 400 ? 'up' : 'down';
      
      const updatedCheck: UptimeCheck = {
        ...newCheck,
        status,
        responseTime,
        statusCode,
        error: status === 'down' ? `HTTP ${statusCode} Error` : undefined
      };

      setChecks(prev => prev.map(check => 
        check.id === newCheck.id ? updatedCheck : check
      ));

      // Update stats
      setStats(prev => {
        const totalChecks = prev.totalChecks + 1;
        const successfulChecks = prev.successfulChecks + (status === 'up' ? 1 : 0);
        const uptime = (successfulChecks / totalChecks) * 100;
        const averageResponseTime = status === 'up' 
          ? (prev.averageResponseTime * (successfulChecks - 1) + responseTime) / successfulChecks
          : prev.averageResponseTime;

        return {
          uptime,
          totalChecks,
          successfulChecks,
          averageResponseTime,
          lastDowntime: status === 'down' ? Date.now() : prev.lastDowntime
        };
      });

      return updatedCheck;
    } catch (error) {
      const errorCheck: UptimeCheck = {
        ...newCheck,
        status: 'down',
        responseTime: Date.now() - startTime,
        statusCode: 0,
        error: 'Network error or invalid URL'
      };

      setChecks(prev => prev.map(check => 
        check.id === newCheck.id ? errorCheck : check
      ));

      return errorCheck;
    }
  };

  // Single check
  const handleSingleCheck = async () => {
    if (!url) {
      toast.error('Please enter a URL to check');
      return;
    }

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('Please enter a valid URL (include http:// or https://)');
      return;
    }

    setIsChecking(true);
    try {
      const result = await checkWebsite();
      toast.success(
        result.status === 'up' 
          ? `Website is up! Response time: ${result.responseTime}ms`
          : `Website is down: ${result.error}`
      );
    } catch (error) {
      console.error('Check failed:', error);
      toast.error('Failed to check website');
    } finally {
      setIsChecking(false);
    }
  };

  // Start monitoring
  const startMonitoring = () => {
    if (!url) {
      toast.error('Please enter a URL to monitor');
      return;
    }

    if (isMonitoring) {
      stopMonitoring();
      return;
    }

    setIsMonitoring(true);
    
    // Immediate check
    checkWebsite();
    
    // Set up interval
    const interval = setInterval(() => {
      checkWebsite();
    }, checkInterval * 1000);
    
    setMonitoringInterval(interval);
    toast.success(`Started monitoring ${url} every ${checkInterval} seconds`);
  };

  // Stop monitoring
  const stopMonitoring = () => {
    if (monitoringInterval) {
      clearInterval(monitoringInterval);
      setMonitoringInterval(null);
    }
    setIsMonitoring(false);
    toast.success('Stopped monitoring');
  };

  // Clear history
  const clearHistory = () => {
    setChecks([]);
    setStats({
      uptime: 0,
      totalChecks: 0,
      successfulChecks: 0,
      averageResponseTime: 0
    });
    toast.success('History cleared');
  };

  // Load example URL
  const loadExample = () => {
    setUrl('https://www.google.com');
    toast.success('Example URL loaded');
  };

  // Format time ago
  const formatTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      case 'checking': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'up': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'down': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'checking': return <RefreshCw className="w-5 h-5 text-yellow-600 animate-spin" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Activity className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Website Uptime Checker</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Monitor your website's availability and performance. Check uptime status, response times, and get real-time monitoring alerts.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* URL Input and Controls */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Website URL</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  onClick={loadExample}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Example
                </button>
              </div>

              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleSingleCheck}
                  disabled={isChecking || !url}
                  className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isChecking ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Globe className="w-4 h-4" />
                  )}
                  Single Check
                </button>

                <button
                  onClick={startMonitoring}
                  disabled={!url}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isMonitoring 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  <Activity className="w-4 h-4" />
                  {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
                </button>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Interval:</label>
                  <select
                    value={checkInterval}
                    onChange={(e) => setCheckInterval(parseInt(e.target.value))}
                    disabled={isMonitoring}
                    className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                  >
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                    <option value={600}>10 minutes</option>
                    <option value={1800}>30 minutes</option>
                    <option value={3600}>1 hour</option>
                  </select>
                </div>

                <button
                  onClick={clearHistory}
                  disabled={checks.length === 0}
                  className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Clear History
                </button>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-8">
            {/* Statistics */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Statistics
                </h3>

                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {stats.uptime.toFixed(1)}%
                    </div>
                    <div className="text-sm text-green-700">Uptime</div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-lg font-semibold text-blue-600">
                        {stats.totalChecks}
                      </div>
                      <div className="text-xs text-blue-700">Total Checks</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="text-lg font-semibold text-purple-600">
                        {stats.successfulChecks}
                      </div>
                      <div className="text-xs text-purple-700">Successful</div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-lg font-semibold text-yellow-600">
                      {stats.averageResponseTime > 0 ? `${Math.round(stats.averageResponseTime)}ms` : 'N/A'}
                    </div>
                    <div className="text-sm text-yellow-700">Average Response Time</div>
                  </div>

                  {stats.lastDowntime && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-red-600">Last Downtime</div>
                      <div className="text-xs text-red-700">
                        {formatTimeAgo(stats.lastDowntime)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Current Status */}
                {checks.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Current Status</h4>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(checks[0].status)}
                      <div>
                        <div className={`font-semibold capitalize ${getStatusColor(checks[0].status)}`}>
                          {checks[0].status}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTimeAgo(checks[0].timestamp)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Monitoring Status */}
              {isMonitoring && (
                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700">
                    <Activity className="w-5 h-5" />
                    <span className="font-semibold">Monitoring Active</span>
                  </div>
                  <div className="text-sm text-green-600 mt-1">
                    Checking every {formatDuration(checkInterval)}
                  </div>
                </div>
              )}
            </div>

            {/* Check History */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Check History
                </h3>

                {checks.length === 0 ? (
                  <div className="text-center py-12">
                    <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No checks performed yet</p>
                    <p className="text-sm text-gray-400">Enter a URL and click "Single Check" or "Start Monitoring"</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {checks.map((check) => (
                      <div key={check.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(check.status)}
                            <div>
                              <div className={`font-semibold capitalize ${getStatusColor(check.status)}`}>
                                {check.status}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatTimeAgo(check.timestamp)}
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            {check.status !== 'checking' && (
                              <>
                                <div className="text-sm font-semibold">
                                  {check.responseTime}ms
                                </div>
                                <div className="text-xs text-gray-500">
                                  HTTP {check.statusCode || 'N/A'}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 truncate">
                          {check.url}
                        </div>

                        {check.error && (
                          <div className="mt-2 text-sm text-red-600 bg-red-50 rounded p-2">
                            {check.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Understanding Uptime</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>99.9% Uptime:</strong> About 8.7 hours downtime per year</li>
                <li>• <strong>99.95% Uptime:</strong> About 4.4 hours downtime per year</li>
                <li>• <strong>99.99% Uptime:</strong> About 52 minutes downtime per year</li>
                <li>• <strong>Response Time:</strong> Time taken to receive first byte</li>
                <li>• <strong>Status Codes:</strong> 2xx = Success, 4xx = Client Error, 5xx = Server Error</li>
                <li>• <strong>Monitoring Frequency:</strong> More frequent checks catch issues faster</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Improving Website Uptime</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Reliable Hosting:</strong> Choose reputable hosting providers</li>
                <li>• <strong>CDN Usage:</strong> Use Content Delivery Networks</li>
                <li>• <strong>Load Balancing:</strong> Distribute traffic across servers</li>
                <li>• <strong>Regular Monitoring:</strong> Set up automated monitoring</li>
                <li>• <strong>Backup Systems:</strong> Have failover mechanisms</li>
                <li>• <strong>Performance Optimization:</strong> Optimize code and databases</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">Common Downtime Causes</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>Server Issues:</strong> Hardware failures or overload</li>
                <li>• <strong>Network Problems:</strong> ISP or DNS issues</li>
                <li>• <strong>Software Bugs:</strong> Application errors or crashes</li>
                <li>• <strong>Maintenance:</strong> Planned or emergency updates</li>
                <li>• <strong>DDoS Attacks:</strong> Malicious traffic overwhelming servers</li>
                <li>• <strong>Human Error:</strong> Configuration mistakes or accidents</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Best Monitoring Practices</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>Multiple Locations:</strong> Monitor from different geographic locations</li>
                <li>• <strong>Various Protocols:</strong> Test HTTP, HTTPS, and other services</li>
                <li>• <strong>Alert Systems:</strong> Set up immediate notifications</li>
                <li>• <strong>Regular Reports:</strong> Generate uptime reports</li>
                <li>• <strong>Escalation Plans:</strong> Define response procedures</li>
                <li>• <strong>Performance Baselines:</strong> Track performance trends</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WebsiteUptimeChecker;
